import {
    convertBecIssuesToNewIssueFormat,
    convertFdIssuesToNewIssueFormat,
} from '@/ui/routes/fraud-defense/utils/utils'
import { BaseModule } from '@core/base/module/base-module'
import { UnifiedFilterModule } from '@core/modules/filters/unified-filter.module'
import { AssetsService } from '@core/services/api-gateway'
import { transformUsersIssues } from '../../issues/transformers'
import { UsersModule } from '../../users'
import { unifiedAssetsConfig } from './assets.filters'

export interface IAssetsState extends IRemoteData {
    data: TAsset[]
}

export const unifiedAssetsFilterModule = new UnifiedFilterModule(
    unifiedAssetsConfig,
    'assets.unified.filter'
)

const state: IAssetsState = {
    data: [],
    lastFetched: 0,
    loading: false,
    error: false,
}

class Assets extends BaseModule<IAssetsState> {
    filterModule: UnifiedFilterModule

    constructor(state: IAssetsState) {
        super(state, 'assets.module')
        this.filterModule = unifiedAssetsFilterModule
    }

    enableIntegrationsFilter = (data: TAsset[]) => {
        return data.some((asset) => asset.asset_type === 'integration')
    }

    async loadAssets(forced?: boolean): Promise<TAsset[] | boolean> {
        if (!this.store.get().lastFetched || forced) {
            this.setLoading(true)

            const result = await AssetsService.getAssets()
            if (!result) {
                this.setLoading(false)
                this.setError(true)
                return false
            }

            const data = result as unknown as TAsset[]
            const usersIssues = transformUsersIssues(
                result.filter((asset) => asset.asset_type === 'user')
            )

            const fdIssues = convertFdIssuesToNewIssueFormat(usersIssues)
            const becIssues = convertBecIssuesToNewIssueFormat(usersIssues)
            UsersModule.setData([...fdIssues, ...becIssues])

            this.setData(data)
            this.setLastFetched()
            this.setLoading(false)
            return data
        }
        return this.store.get().data
    }

    setData(data: TAsset[]) {
        this.store.setKey('data', data)
    }

    setLastFetched() {
        this.store.setKey('lastFetched', Date.now())
    }

    setLoading(loading: boolean) {
        this.store.setKey('loading', loading)
    }

    setError(error: boolean) {
        this.store.setKey('error', error)
    }

    getData(disableFilters = false): TAsset[] {
        return this.filterModule.processData(
            this.store.get().data,
            disableFilters
        )
    }

    getFullData(): TAsset[] {
        return this.store.get().data
    }

    getFilterModule(): UnifiedFilterModule {
        return this.filterModule
    }

    getUsersAssetsWithFdIssues(): TAsset[] {
        const usersAssets = this.useStore().data.filter(
            (asset) => asset.asset_type === 'user'
        )
        return usersAssets.filter((asset) =>
            asset.issues?.some(
                (issue: TIssue) => issue.issue_type === 'FRAUD_DEFENSE'
            )
        )
    }

    getCloudApps(): TAssetCloudApp[] {
        return this.useStore().data.filter(
            (asset) => asset.asset_type === 'cloud_app'
        )
    }
}

export const AssetsModule = new Assets(state)
export type TAssetsModule = typeof AssetsModule
